// SmsNumberService — which number a company texts from, and what that permits.
//
// TWO TIERS (docs/SMS_PHASE4_SOLO_GOAL.md):
//   'shared' — the platform toll-free number. One-way, APP TEMPLATES ONLY.
//   'own'    — the company's own verified number. Two-way, free text.
//
// The tier is derived from `company_phone_numbers`, never stored on the client
// and never trusted as an authorisation: send-sms re-derives it server-side on
// every send. What this service is for is telling the UI what to OFFER, so a
// contractor is not invited to type a message the server will refuse.
//
// The row is read-only to tenants by design — it is written exclusively by the
// provisioning edge functions under the service-role key. A client-writable
// verification_status would let a company mark itself approved and send
// unverified traffic on the platform's Twilio account.

import { supabase } from '../utils/supabaseClient';
import settingsService from './SettingsService';

// Mirrors SHARED_NUMBER_TEMPLATES in supabase/functions/send-sms/index.ts.
// Kept here only so the UI can explain what the free tier covers; the server
// list is authoritative.
export const SHARED_NUMBER_TEMPLATES = [
  'job_reminder',
  'quote_notification',
  'invoice_reminder'
];

// Twilio's rejection codes, mapped to something a contractor can act on. The
// raw reasons are persisted verbatim in company_phone_numbers.rejection_reasons
// so a code we have not mapped yet is still recoverable rather than lost.
const REJECTION_HELP = {
  30484: "The business name doesn't match your website. Enter your name exactly as it appears there, and tell us your DBA if you trade under a different one.",
  30473: "We couldn't load your website. It has to be public — no login, no under-construction placeholder.",
  30492: 'Your website needs to clearly show your business name.',
  30445: "We couldn't confirm your business details. Check the legal name, address and registration number.",
  30506: 'Your opt-in wording needs to name your business, not ours.',
  30507: "Your opt-in wording doesn't cover the kind of messages you plan to send."
};

class SmsNumberService {
  /**
   * The company's number row, or null when they are on the shared number.
   * Null is the normal case, not an error — most companies never upgrade.
   */
  async getNumber(companyId) {
    try {
      const { data, error } = await supabase
        .from('company_phone_numbers')
        .select(
          'id, e164, status, number_type, verification_status, ' +
          'brand_status, campaign_status, rejection_reasons, ' +
          'provisioned_at, verified_at, released_at, release_after, monthly_cost_cents'
        )
        .eq('company_id', companyId)
        .neq('status', 'released')
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    } catch (error) {
      console.error('❌ SmsNumberService.getNumber error:', error);
      // Report "no number" rather than a false positive. Claiming the company
      // owns a number it cannot prove would let the UI offer free text that
      // send-sms then refuses — a worse experience than the honest tier.
      return null;
    }
  }

  /**
   * 'own' only when the number is both live AND verified by Twilio. A purchased
   * but unverified number cannot carry traffic, so it is still the shared tier
   * as far as what the contractor may send.
   */
  isLive(row) {
    return row?.status === 'active' && row?.verification_status === 'approved';
  }

  async getTier(companyId) {
    const row = await this.getNumber(companyId);
    const isLive = this.isLive(row);
    return {
      tier: isLive ? 'own' : 'shared',
      number: row,
      live: isLive,
      canSendFreeText: isLive,
      canReceiveReplies: isLive
    };
  }

  /**
   * THE single answer to "may this company send SMS" (board #4784).
   * Live number (getTier) AND the company's own preference switched on. Every
   * consumer derives from here rather than re-reading company_phone_numbers or
   * company_settings.sms_notifications_enabled on its own, so the two can never
   * disagree. Reports false on any error — same safe default as getNumber().
   */
  async canSendSms(companyId) {
    if (!companyId) return false;
    try {
      const [tierInfo, settings] = await Promise.all([
        this.getTier(companyId),
        settingsService.getSettings(companyId)
      ]);
      return tierInfo.live === true && settings?.sms_notifications_enabled === true;
    } catch (error) {
      console.error('❌ SmsNumberService.canSendSms error:', error);
      return false;
    }
  }

  /**
   * Plain-English rejection reasons for the status screen.
   * Falls back to Twilio's own description when the code is unmapped, so a new
   * code degrades to "less friendly" rather than "blank screen".
   */
  describeRejections(rejectionReasons) {
    if (!Array.isArray(rejectionReasons)) return [];
    return rejectionReasons.map((r) => {
      const code = r?.code ?? r?.error_code;
      return {
        code: code ?? null,
        message:
          REJECTION_HELP[code] ||
          r?.description ||
          r?.message ||
          'This needs another look before we can resubmit.'
      };
    });
  }

  /**
   * Turn the monthly number charge on or off.
   * Billing runs through stripe-subscribe because that is the only writer of the
   * Stripe subscription — a second writer is how seat quantities and add-ons
   * drift apart. Must succeed before a number can be bought; the purchase
   * function re-checks server-side rather than trusting this call happened.
   */
  async setAddonEnabled(enable) {
    return this._invoke('stripe-subscribe', { action: 'sms-number-addon', enable });
  }

  /**
   * May this company buy its own number? The same rule twilio-provision-number asks
   * before purchasing (stripe-subscribe smsNumberAddonEligibility). Resolves to
   * `{ eligible: true }`, `{ eligible: false, error, billingExempt?, needsSubscription?,
   * notLaunched? }`, or `null` when the answer could not be read.
   */
  async getPurchaseEligibility() {
    const result = await this._invoke('stripe-subscribe', { action: 'sms-number-eligibility' });
    return result?.success === true && typeof result.eligible === 'boolean' ? result : null;
  }

  /**
   * Available numbers. Searching costs nothing.
   *
   * `numberType` decides which POOL is searched, and they behave differently:
   * toll-free numbers have no local area code (only 800/833/844/855/866/877/888),
   * whereas a local search without an area code is meaningless — the whole point
   * of a local number is the area code.
   */
  async searchNumbers({ areaCode, contains, numberType = 'toll_free' } = {}) {
    return this._invoke('twilio-provision-number', {
      action: 'list', areaCode, contains, numberType
    });
  }

  /** Buy a specific number. THIS SPENDS MONEY and starts a recurring charge. */
  async buyNumber(e164, numberType = 'toll_free') {
    return this._invoke('twilio-provision-number', { action: 'buy', e164, numberType });
  }

  /** Create the Trust Hub secondary profile the verification is submitted under. */
  async createComplianceProfile() {
    return this._invoke('twilio-provision-profile', {});
  }

  /** Submit (or resubmit) toll-free verification. */
  async submitVerification() {
    return this._invoke('twilio-submit-verification', {});
  }

  /**
   * Save the toll-free compliance answers.
   *
   * Only the SMS-specific fields live in company_sms_compliance. Legal name,
   * website, address and contact stay on `companies`, and the EIN stays in
   * companies.tax_id behind update_company_tax_id() — there is deliberately no
   * second copy of any of them here.
   */
  async saveCompliance(companyId, fields) {
    try {
      const { error } = await supabase
        .from('company_sms_compliance')
        .upsert(
          { company_id: companyId, ...fields, updated_at: new Date().toISOString() },
          { onConflict: 'company_id' }
        );
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('❌ SmsNumberService.saveCompliance error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save the company's EIN.
   *
   * A PLAIN TABLE UPDATE, deliberately — not `update_company_tax_id`.
   *
   * Encryption is not this function's job and never was: the trigger
   * `encrypt_tax_id_trigger` (BEFORE INSERT OR UPDATE on `companies`) runs
   * `auto_encrypt_tax_id()`, which writes `encrypted_tax_id` and then sets
   * `tax_id` back to NULL so no plaintext is ever stored. That happens for ANY
   * writer, so a normal update is both sufficient and the canonical path.
   *
   * The RPC was dropped for two independent reasons. It 404s at PostgREST for
   * reasons that never resolved (the function exists, executes, and grants
   * EXECUTE to authenticated — a schema-cache reload did not fix it). And it
   * authorised via `profiles.role`, a column `profiles` does not have, so it
   * raised for every user from the day it was written. Rather than keep
   * debugging a function nothing else calls, the write now uses the same
   * mechanism that already saves `legal_name` two lines away — one write path,
   * RLS-gated by `companies_update_own`.
   *
   * Reading it back requires DECRYPTING `encrypted_tax_id`; `tax_id` is always
   * NULL. Server-side callers must do that rather than reading the column.
   */
  async saveEin(companyId, taxId) {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ tax_id: taxId })
        .eq('id', companyId);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('❌ SmsNumberService.saveEin error:', error);
      return { success: false, error: error.message };
    }
  }

  async getCompliance(companyId) {
    try {
      const { data, error } = await supabase
        .from('company_sms_compliance')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    } catch (error) {
      console.error('❌ SmsNumberService.getCompliance error:', error);
      return null;
    }
  }

  /**
   * Edge-function calls surface the real error body rather than supabase-js's
   * generic "non-2xx status code", so a 402 needsBilling or a 400 listing the
   * missing fields reaches the UI intact. Same shape as SmsCreditsService.
   */
  async _invoke(fn, body) {
    try {
      const { data, error } = await supabase.functions.invoke(fn, { body });
      if (error) {
        let payload = null;
        try {
          payload = await error.context?.json();
        } catch {
          // Non-JSON body (gateway error, timeout) — fall through.
        }
        return {
          success: false,
          error: payload?.error || error.message || 'Something went wrong',
          ...payload
        };
      }
      return data;
    } catch (error) {
      console.error(`❌ SmsNumberService.${fn} error:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Human label for the registration state, for a status chip.
   *
   * The two tiers register differently and a single status cannot describe both.
   * Toll-free has ONE outcome (Toll-Free Verification). Local has TWO — a brand
   * and a campaign — and either can fail on its own, so a local number showing
   * "Active" while its campaign is still pending would be a lie that ends in
   * unregistered traffic being filtered by carriers.
   */
  describeStatus(row) {
    if (!row) return { label: 'Shared number', tone: 'neutral' };
    if (row.status === 'released') return { label: 'Released', tone: 'neutral' };

    // "Active" means exactly what getTier() means by it — status 'active' AND
    // verification 'approved' — so the chip can never promise a tier the sender refuses.
    if (this.isLive(row)) return { label: 'Active', tone: 'success' };

    if (row.number_type === 'local') {
      if (row.brand_status === 'failed') return { label: 'Business check failed', tone: 'error' };
      if (row.campaign_status === 'failed') return { label: 'Campaign rejected', tone: 'error' };
      if (row.brand_status === 'approved' && row.campaign_status === 'approved') {
        return { label: 'Registered, awaiting activation', tone: 'pending' };
      }
      if (row.brand_status === 'approved') {
        return { label: 'Campaign under review', tone: 'pending' };
      }
      if (row.brand_status) return { label: 'Business under review', tone: 'pending' };
      return { label: 'Setting up', tone: 'pending' };
    }

    switch (row.verification_status) {
      case 'approved':
        return { label: 'Approved, awaiting activation', tone: 'pending' };
      case 'rejected':
        return { label: 'Needs attention', tone: 'error' };
      case 'pending_review':
      case 'in_review':
        return { label: 'Pending verification', tone: 'pending' };
      default:
        return { label: 'Setting up', tone: 'pending' };
    }
  }
}

const smsNumberService = new SmsNumberService();
export default smsNumberService;
